import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  IconButton,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StarIcon from "@mui/icons-material/Star";
import { getUniquePlanFeatures } from "./utils/plan-utils";

const PLAN_NAMES = {
  freemium: "Freemium",
  standard: "Standard",
  enterprise: "Enterprise",
  franquicia: "Franquicia",
};

const PLAN_PRICES = {
  freemium: { monthly: 150, yearly: 1800 }, // 1800 BOB por año
  standard: { monthly: 292, yearly: 3500 }, // 3500 BOB por año
  franquicia: { monthly: 292, yearly: 3500 }, // 3500 BOB por año + 100 BOB por sucursal
  enterprise: { monthly: 458, yearly: 5300 }, // 5300 BOB por año
};

const StyledDialog = styled(Dialog)(() => ({
  "& .MuiDialog-paper": {
    maxWidth: "1400px",
    width: "95%",
    borderRadius: "12px",
    padding: "0",
    backgroundColor: "#fafafa",
  },
}));

const StyledDialogTitle = styled(DialogTitle)(() => ({
  background: "linear-gradient(135deg, #252526 0%, #404040 100%)",
  color: "white",
  padding: "20px 32px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
}));

const PlanCard = styled(Card)(({ isrecommended }) => ({
  height: "100%",
  border: isrecommended === "true" ? `3px solid #FF770D` : `1px solid #e0e0e0`,
  borderRadius: "8px",
  transition: "all 0.2s ease",
  position: "relative",
  backgroundColor: "white",
  display: "flex",
  flexDirection: "column",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
    borderColor: isrecommended === "true" ? "#FF770D" : "#cccccc",
  },
}));

const PlanHeader = styled(Box)(({ isrecommended }) => ({
  background: isrecommended === "true" ? "linear-gradient(135deg, #FF770D 0%, #e5660a 100%)" : "#252526",
  color: "white",
  padding: "20px 24px",
  textAlign: "center",
  borderRadius: "6px 6px 0 0",
}));

const PriceBox = styled(Box)(() => ({
  textAlign: "center",
  padding: "12px 16px",
  backgroundColor: "#f8f9fa",
  borderRadius: "6px",
  margin: "16px 0",
  border: "1px solid #e9ecef",
}));

const FeatureList = styled(List)(() => ({
  padding: "8px 0",
  "& .MuiListItem-root": {
    paddingTop: "6px",
    paddingBottom: "6px",
    paddingLeft: "0",
    paddingRight: "0",
  },
}));

const SelectButton = styled(Button)(({ isrecommended }) => ({
  backgroundColor: isrecommended === "true" ? "#FF770D" : "#252526",
  color: "white",
  fontWeight: "600",
  padding: "10px 20px",
  borderRadius: "6px",
  width: "100%",
  textTransform: "none",
  fontSize: "14px",
  "&:hover": {
    backgroundColor: isrecommended === "true" ? "#e5660a" : "#404040",
  },
}));

const PopularBadge = styled(Chip)(() => ({
  position: "absolute",
  top: "-10px",
  right: "16px",
  backgroundColor: "#FF770D",
  color: "white",
  fontWeight: "bold",
  "& .MuiChip-icon": {
    color: "white",
  },
}));

const PlanSelectionModal = ({ open, onClose, onSelectPlan, isForced = false }) => {
  const planKeys = Object.keys(PLAN_NAMES);

  const handleSelectPlan = (planKey) => {
    onSelectPlan(planKey);
    if (!isForced) {
      onClose();
    }
  };

  const getPlanFeatures = (planKey) => {
    return getUniquePlanFeatures(planKey);
  };

  const isPopular = (planKey) => planKey === "enterprise";

  return (
    <StyledDialog open={open} onClose={isForced ? undefined : onClose} maxWidth="xl">
      <StyledDialogTitle>
        <Box>
          <Typography variant="h4" component="h2" sx={{ fontWeight: "bold", mb: 1 }}>
            {isForced ? "¡Tu período de prueba ha expirado!" : "Selecciona tu Plan"}
          </Typography>
          <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
            {isForced
              ? "Para continuar usando nuestros servicios, selecciona un plan que se adapte a tu negocio"
              : "Elige el plan que mejor se adapte a las necesidades de tu negocio"}
          </Typography>
        </Box>
        {!isForced && (
          <IconButton onClick={onClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        )}
      </StyledDialogTitle>

      <DialogContent sx={{ padding: "40px", mt: "10px" }}>
        <Grid container spacing={4}>
          {planKeys.map((planKey) => (
            <Grid item xs={12} sm={6} md={3} key={planKey}>
              <PlanCard
                isrecommended={isPopular(planKey) ? "true" : "false"}
                onClick={() => {
                  const planName = PLAN_NAMES[planKey];
                  const mensaje = encodeURIComponent(
                    `Hola, estoy interesado en adquirir el plan ${planName} para mi empresa. ¿Podrían brindarme más información y asesoría?`
                  );
                  window.open(`https://wa.me/59177142692?text=${mensaje}`, "_blank");
                }}
                sx={{ cursor: "pointer" }}
              >
                {isPopular(planKey) && <PopularBadge icon={<StarIcon />} label="Recomendado" />}

                <PlanHeader isrecommended={isPopular(planKey) ? "true" : "false"}>
                  <Typography variant="h6" sx={{ fontWeight: "bold", mb: 0.5 }}>
                    {PLAN_NAMES[planKey]}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9, fontSize: "12px" }}>
                    {planKey === "freemium" && "Plan básico para negocios pequeños"}
                    {planKey === "standard" && "Perfecto para negocios en crecimiento"}
                    {planKey === "franquicia" && "Como Standard + facturación por sucursal"}
                    {planKey === "enterprise" && "Solución completa para empresas grandes"}
                  </Typography>
                </PlanHeader>

                <CardContent sx={{ padding: "24px", flexGrow: 1, display: "flex", flexDirection: "column" }}>
                  <PriceBox>
                    <Typography variant="h4" sx={{ fontWeight: "bold", color: "#252526" }}>
                      {PLAN_PRICES[planKey].yearly} Bs
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ fontSize: "12px" }}>
                      {planKey === "franquicia" ? "por año" : "por año"}
                    </Typography>
                    {planKey === "franquicia" ? (
                      <Typography variant="caption" color="textSecondary" sx={{ fontSize: "10px", fontWeight: "600" }}>
                        + 100 Bs por cada sucursal
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="textSecondary" sx={{ fontSize: "10px" }}>
                        Equivale a {Math.round(PLAN_PRICES[planKey].yearly / 12)} Bs/mes
                      </Typography>
                    )}
                  </PriceBox>

                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: "600", color: "#252526" }}>
                    {planKey === "freemium" ? "Funcionalidades básicas:" : "Lo nuevo en este plan:"}
                  </Typography>

                  <Box sx={{ flexGrow: 1 }}>
                    <FeatureList>
                      {getPlanFeatures(planKey)
                        .slice(0, 5)
                        .map((feature, index) => (
                          <ListItem key={index} sx={{ px: 0 }}>
                            <ListItemIcon sx={{ minWidth: "24px" }}>
                              <CheckCircleIcon sx={{ color: "#FF770D", fontSize: "16px" }} />
                            </ListItemIcon>
                            <ListItemText
                              primary={feature}
                              primaryTypographyProps={{
                                fontSize: "13px",
                                lineHeight: 1.4,
                              }}
                            />
                          </ListItem>
                        ))}
                      {getPlanFeatures(planKey).length > 5 && (
                        <ListItem sx={{ px: 0 }}>
                          <ListItemIcon sx={{ minWidth: "24px" }}>
                            <CheckCircleIcon sx={{ color: "#FF770D", fontSize: "16px" }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={`+${getPlanFeatures(planKey).length - 5} características más`}
                            primaryTypographyProps={{
                              fontSize: "12px",
                              fontStyle: "italic",
                              color: "#666",
                            }}
                          />
                        </ListItem>
                      )}
                      {planKey !== "freemium" && (
                        <ListItem sx={{ px: 0, mt: 1 }}>
                          <ListItemText
                            primary="+ Todo lo incluido en planes anteriores"
                            primaryTypographyProps={{
                              fontSize: "11px",
                              fontStyle: "italic",
                              color: "#888",
                              fontWeight: "500",
                            }}
                          />
                        </ListItem>
                      )}
                    </FeatureList>
                  </Box>

                  <Box sx={{ mt: "auto", pt: 2 }}>
                    <SelectButton
                      isrecommended={isPopular(planKey) ? "true" : "false"}
                      onClick={() => {
                        const planName = PLAN_NAMES[planKey];
                        const mensaje = encodeURIComponent(
                          `Hola, estoy interesado en adquirir el plan ${planName} para mi empresa. ¿Podrían brindarme más información y asesoría?`
                        );
                        window.open(`https://wa.me/59177142692?text=${mensaje}`, "_blank");
                      }}
                      variant="contained"
                    >
                      Seleccionar Plan
                    </SelectButton>
                  </Box>
                </CardContent>
              </PlanCard>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ textAlign: "center", mt: 3, p: 2, backgroundColor: "#f8f9fa", borderRadius: "8px" }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "600", color: "#252526" }}>
            ¿Necesitas ayuda para elegir?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2, fontSize: "13px" }}>
            Nuestro equipo está aquí para ayudarte a encontrar el plan perfecto
          </Typography>
          <Button
            variant="outlined"
            size="small"
            sx={{
              borderColor: "#FF770D",
              color: "#FF770D",
              fontSize: "12px",
              padding: "6px 16px",
              "&:hover": {
                backgroundColor: "#FF770D",
                color: "white",
              },
            }}
          >
            Contactar a Ventas
          </Button>
        </Box>
      </DialogContent>
    </StyledDialog>
  );
};

export default PlanSelectionModal;
